import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EButton,
  ECancelButton,
  EDangerButton,
  ETableSingleAction,
  IconCell,
  SimpleTable,
  SimpleTableColumn,
} from 'webapp-lib/Components';

import {Dialog, DialogProps} from '#';
import {DateAndUser} from '#/DateAndUser';
import {UserInitialsWithTooltip} from '#/UserInitialsWithTooltip';
import {LockedDocumentInResponse} from '@lexian/api/documents-api';
import {UnknownDocument} from '_/assets/images';
import loading from '_/assets/images/refresh.svg';
import {DOCUMENT_ICONS_MAP} from '_/constants';
import {I18NextNS} from '_/i18n';
import {openDocument} from '_/utilities';

import {DialogType} from '../store/filesInUseUtils';

import {
  InfoText,
  RefreshCounterMessage,
  RefreshCounterWrapper,
  WarningDescription,
} from './FilesInUse.styles';

const REFRESH_INTERVAL_IN_SECONDS = 5000;

type FilesInUseProps = {
  dialogType: DialogType;
  filesInUse: LockedDocumentInResponse[];
  hasFilesInUse: boolean;
  onClearAllFiles: () => void;
  onLogout: () => Promise<void>;
  onQuitAnyway: () => void;
  onQuitRegularly: () => void;
  onRefreshLockedDocuments: () => Promise<void>;
  onReleaseAllStaleDocuments: () => Promise<void>;
  onReleaseDocument: (document: LockedDocumentInResponse) => Promise<void>;
  onTryClosingAgain: () => Promise<void>;
  onTryUpdatingAgain: () => Promise<void>;
  onUpdate: () => void;
  watchedDocumentIds: Set<string> | undefined;
};

const FilesInUse = ({
  dialogType,
  filesInUse,
  hasFilesInUse,
  onClearAllFiles,
  onLogout,
  onQuitAnyway,
  onQuitRegularly,
  onRefreshLockedDocuments,
  onReleaseAllStaleDocuments,
  onReleaseDocument,
  onTryClosingAgain,
  onTryUpdatingAgain,
  onUpdate,
  watchedDocumentIds,
}: FilesInUseProps): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('documents');
  const [countdown, setCountdown] = useState<number>(0);

  // `undefined` means the watched ids are unknown (browser has no document
  // watcher, or Electron did not answer) — treat all documents as watched
  // so they keep the full action set and exit flows behave as before
  const isDocumentWatched = useCallback(
    (document: LockedDocumentInResponse) =>
      !watchedDocumentIds || watchedDocumentIds.has(document.documentId),
    [watchedDocumentIds]
  );

  const dialogTypeWithRefresh = useMemo(
    () =>
      dialogType === 'LOGGING_OUT' ||
      dialogType === 'CLOSING_APP' ||
      dialogType === 'UPDATING_APP' ||
      dialogType === 'FROM_MENU',
    [dialogType]
  );

  useEffect(() => {
    if (dialogTypeWithRefresh) {
      setCountdown(REFRESH_INTERVAL_IN_SECONDS / 1000);
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      const interval = setInterval(async () => {
        await onRefreshLockedDocuments();
        setCountdown(REFRESH_INTERVAL_IN_SECONDS / 1000);
      }, REFRESH_INTERVAL_IN_SECONDS);
      return () => clearInterval(interval);
    }
  }, [dialogTypeWithRefresh, onRefreshLockedDocuments]);

  useEffect(() => {
    if (dialogTypeWithRefresh) {
      const intervalCountdown = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearInterval(intervalCountdown);
    }
  }, [dialogTypeWithRefresh, countdown]);

  // Locks from other workstations cannot be influenced by exiting this app,
  // so only locally watched documents keep the default action pending
  const hasWatchedFilesInUse = filesInUse.some(isDocumentWatched);

  useEffect(() => {
    // Trigger second timer when all files have been released and execute default action
    if (dialogTypeWithRefresh && !hasWatchedFilesInUse) {
      if (dialogType === 'LOGGING_OUT') {
        onLogout();
      } else if (dialogType === 'CLOSING_APP') {
        onQuitRegularly();
      }
    }
  }, [
    dialogTypeWithRefresh,
    dialogType,
    hasWatchedFilesInUse,
    onLogout,
    onQuitRegularly,
  ]);

  const refreshCounter = (
    <RefreshCounterWrapper key='counter'>
      <object
        aria-labelledby='countdown-message'
        data={loading}
        tabIndex={-1}
        type='image/svg+xml'
      ></object>
      <RefreshCounterMessage id='countdown-message'>
        {t('filesInUse.countdown', {
          seconds: countdown,
        })}
      </RefreshCounterMessage>
    </RefreshCounterWrapper>
  );

  const HEADERS: Record<DialogType, string> = {
    IN_AN_ACTION: t('filesInUse.header.InAnAction'),
    LAST_LOGIN: t('filesInUse.header.LastLogin'),
    FROM_MENU: t('filesInUse.header.FromMenu'),
    LOGGING_OUT: t('filesInUse.header.LoggingOut'),
    LOGGING_OUT_UNREACHABLE: t('filesInUse.header.LoggingOutUnreachable'),
    CLOSING_APP: t('filesInUse.header.ClosingApp'),
    CLOSING_APP_UNREACHABLE: t('filesInUse.header.ClosingAppUnreachable'),
    CLOSING_APP_UNAUTHORIZED: t('filesInUse.header.ClosingAppUnauthorized'),
    UPDATING_APP: t('filesInUse.header.UpdatingApp'),
    UPDATING_APP_UNREACHABLE: t('filesInUse.header.UpdatingAppUnreachable'),
    UPDATING_APP_UNAUTHORIZED: t('filesInUse.header.UpdatingAppUnauthorized'),
  };

  const showRefresh = dialogTypeWithRefresh && filesInUse.length > 0;

  const dialogProps: DialogProps = {
    actions: [
      ...(showRefresh ? [refreshCounter] : []),
      ...{
        IN_AN_ACTION: [
          <EButton key='close' onClick={onClearAllFiles}>
            {t('common:actions.close')}
          </EButton>,
        ],
        LAST_LOGIN: [
          <EButton
            key='release-all'
            disabled={!hasFilesInUse}
            onClick={onReleaseAllStaleDocuments}
          >
            {t('filesInUse.actions.releaseAllStale')}
          </EButton>,
          <EButton key='close' onClick={onClearAllFiles}>
            {t('common:actions.close')}
          </EButton>,
        ],
        FROM_MENU: [
          <EButton
            key='release-all'
            disabled={!hasFilesInUse}
            onClick={onReleaseAllStaleDocuments}
          >
            {t('filesInUse.actions.releaseAll')}
          </EButton>,
          <EButton key='close' onClick={onClearAllFiles}>
            {t('common:actions.close')}
          </EButton>,
        ],
        LOGGING_OUT: [
          <ECancelButton key='cancel' onClick={onClearAllFiles} />,
          <EButton
            key='logout'
            variant={hasFilesInUse ? 'danger' : undefined}
            onClick={onLogout}
          >
            {t('filesInUse.actions.logout')}
          </EButton>,
        ],
        LOGGING_OUT_UNREACHABLE: [
          <EButton key='try-again' onClick={onTryClosingAgain}>
            {t('filesInUse.actions.try-again')}
          </EButton>,
          <EDangerButton key='quit-anyways' onClick={onQuitAnyway}>
            {t('filesInUse.actions.logout-anyways')}
          </EDangerButton>,
        ],
        CLOSING_APP: [
          <ECancelButton key='cancel' onClick={onClearAllFiles} />,
          <EButton
            key='quit'
            variant={hasFilesInUse ? 'danger' : undefined}
            onClick={onQuitAnyway}
          >
            {t('common:actions.close')}
          </EButton>,
        ],
        CLOSING_APP_UNREACHABLE: [
          <EButton key='try-again' onClick={onTryClosingAgain}>
            {t('filesInUse.actions.try-again')}
          </EButton>,
          <EDangerButton key='quit-anyways' onClick={onQuitAnyway}>
            {t('filesInUse.actions.quit-anyways')}
          </EDangerButton>,
        ],
        CLOSING_APP_UNAUTHORIZED: [
          <EButton key='back' onClick={onClearAllFiles}>
            {t('common:actions.back')}
          </EButton>,
          <EDangerButton key='quit-anyways' onClick={onQuitAnyway}>
            {t('filesInUse.actions.quit-anyways')}
          </EDangerButton>,
        ],
        UPDATING_APP: [
          <ECancelButton key='cancel' onClick={onClearAllFiles} />,
          <EButton
            key='update'
            variant={hasFilesInUse ? 'danger' : undefined}
            onClick={onUpdate}
          >
            {t('common:actions.update')}
          </EButton>,
        ],
        UPDATING_APP_UNREACHABLE: [
          <EButton key='try-again' onClick={onTryUpdatingAgain}>
            {t('filesInUse.actions.try-again')}
          </EButton>,
          <EDangerButton key='update-anyways' onClick={onUpdate}>
            {t('filesInUse.actions.update-anyways')}
          </EDangerButton>,
        ],
        UPDATING_APP_UNAUTHORIZED: [
          <EButton key='back' onClick={onClearAllFiles}>
            {t('common:actions.back')}
          </EButton>,
          <EDangerButton key='update-anyways' onClick={onUpdate}>
            {t('filesInUse.actions.update-anyways')}
          </EDangerButton>,
        ],
      }[dialogType],
    ],
    headerText: HEADERS[dialogType],
    isShown: true,
    onClose: onClearAllFiles,
    size: 'medium',
  };

  const columns = useMemo(
    (): SimpleTableColumn<LockedDocumentInResponse>[] => [
      {
        name: 'documentType',
        cell: (item) => (
          <IconCell>{DOCUMENT_ICONS_MAP[item.documentType]}</IconCell>
        ),
        label: t('filesInUse.column.type'),
        mode: 'min',
      },
      {
        name: 'displayName',
        cell: (item) => item.displayName,
        label: t('filesInUse.column.title'),
        mode: 'break',
      },
      {
        name: 'casusDisplayName',
        cell: (item) => item.casusDisplayName,
        label: t('filesInUse.column.casus'),
        mode: 'break',
      },
      {
        name: 'inUseBy',
        cell: (item) => (
          <UserInitialsWithTooltip
            displayName={item.inUseBy.displayName}
            initials={item.inUseBy.initialLetters}
          />
        ),
        label: t('filesInUse.column.inUseBy'),
      },
      {
        name: 'lastModified',
        cell: (item) => (
          <DateAndUser
            date={item.lastModifiedAt}
            dateFormat='withoutTimeAndTwoDigitYear'
            user={item.lastModifiedBy}
          />
        ),
        label: t('filesInUse.column.lastModified'),
      },
      {
        name: 'created',
        cell: (item) => (
          <DateAndUser
            date={item.createdAt}
            dateFormat='withoutTimeAndTwoDigitYear'
            user={item.createdBy}
          />
        ),
        label: t('filesInUse.column.createdAt'),
      },
    ],
    [t]
  );

  const singleActions = useMemo(() => {
    const openListAction = {
      id: 'open',
      Icon: UnknownDocument,
      callback: (document: LockedDocumentInResponse): void =>
        openDocument({...document, id: document.documentId}, {readonly: false}),
      text: t('filesInUse.actions.open'),
    };

    const openReadOnlyListAction = {
      id: 'open-readonly',
      Icon: UnknownDocument,
      inactive: false,
      callback: (document: LockedDocumentInResponse): void =>
        openDocument({...document, id: document.documentId}, {readonly: true}),
      text: t('filesInUse.actions.openReadOnly'),
    };

    const releaseListAction = {
      id: 'release',
      Icon: UnknownDocument,
      callback: async (document: LockedDocumentInResponse): Promise<void> => {
        await onReleaseDocument(document);
      },
      text: t('filesInUse.actions.release'),
    };

    const watchedDocumentActions = {
      IN_AN_ACTION: [openReadOnlyListAction],
      LAST_LOGIN: [releaseListAction, openListAction],
      FROM_MENU: [releaseListAction, openListAction],
      LOGGING_OUT: [releaseListAction],
      LOGGING_OUT_UNREACHABLE: [],
      CLOSING_APP: [releaseListAction],
      CLOSING_APP_UNREACHABLE: [],
      CLOSING_APP_UNAUTHORIZED: [],
      UPDATING_APP: [],
      UPDATING_APP_UNAUTHORIZED: [],
      UPDATING_APP_UNREACHABLE: [],
    }[dialogType];

    // Documents not watched on this workstation can only be released.
    // IN_AN_ACTION is exempt: it shows a document locked by another user,
    // which is never watched locally but must keep its read-only action.
    return (item: LockedDocumentInResponse): ETableSingleAction[] =>
      dialogType !== 'IN_AN_ACTION' && !isDocumentWatched(item)
        ? [releaseListAction]
        : watchedDocumentActions;
  }, [dialogType, isDocumentWatched, onReleaseDocument, t]);

  let dialogContent;
  if (filesInUse.length > 0) {
    dialogContent = (
      <>
        <WarningDescription>
          {
            {
              IN_AN_ACTION: t('filesInUse.description.InAnAction'),
              LAST_LOGIN: t('filesInUse.description.LastLogin'),
              FROM_MENU: t('filesInUse.description.FromMenu'),
              LOGGING_OUT: t('filesInUse.description.LoggingOut'),
              LOGGING_OUT_UNREACHABLE: t(
                'filesInUse.description.LoggingOutUnreachable'
              ),
              CLOSING_APP: t('filesInUse.description.ClosingApp'),
              CLOSING_APP_UNREACHABLE: t(
                'filesInUse.description.ClosingAppUnreachable'
              ),
              CLOSING_APP_UNAUTHORIZED: t(
                'filesInUse.description.ClosingAppUnauthorized'
              ),
              UPDATING_APP: t('filesInUse.description.UpdatingApp'),
              UPDATING_APP_UNREACHABLE: t(
                'filesInUse.description.UpdatingAppUnreachable'
              ),
              UPDATING_APP_UNAUTHORIZED: t(
                'filesInUse.description.UpdatingAppUnauthorized'
              ),
            }[dialogType]
          }
        </WarningDescription>
        <SimpleTable
          columns={columns}
          data={filesInUse}
          dataId={(document) => document.documentId}
          singleActions={singleActions}
          styleType={(item) =>
            dialogType !== 'IN_AN_ACTION' && !isDocumentWatched(item)
              ? 'disabled'
              : undefined
          }
        />
        {showRefresh ? (
          <InfoText>{t('filesInUse.infoDuration')}</InfoText>
        ) : null}
      </>
    );
  } else {
    dialogContent = (
      <div>
        {
          {
            IN_AN_ACTION: t('filesInUse.descriptionDefaultAction.InAnAction'),
            LAST_LOGIN: t('filesInUse.descriptionDefaultAction.LastLogin'),
            FROM_MENU: t('filesInUse.descriptionDefaultAction.FromMenu'),
            LOGGING_OUT: t('filesInUse.descriptionDefaultAction.LoggingOut'),
            LOGGING_OUT_UNREACHABLE: t(
              'filesInUse.descriptionDefaultAction.LoggingOutUnreachable'
            ),
            CLOSING_APP: t('filesInUse.descriptionDefaultAction.ClosingApp'),
            CLOSING_APP_UNREACHABLE: t(
              'filesInUse.descriptionDefaultAction.ClosingAppUnreachable'
            ),
            CLOSING_APP_UNAUTHORIZED: t(
              'filesInUse.descriptionDefaultAction.ClosingAppUnauthorized'
            ),
            UPDATING_APP: t('filesInUse.descriptionDefaultAction.UpdatingApp'),
            UPDATING_APP_UNREACHABLE: t(
              'filesInUse.descriptionDefaultAction.UpdatingAppUnreachable'
            ),
            UPDATING_APP_UNAUTHORIZED: t(
              'filesInUse.descriptionDefaultAction.UpdatingAppUnauthorized'
            ),
          }[dialogType]
        }
      </div>
    );
  }
  return <Dialog {...dialogProps}>{dialogContent}</Dialog>;
};

export default FilesInUse;
